import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [setupToken, setSetupToken] = useState(localStorage.getItem("setup_token"));
  const [mfaState, setMfaState] = useState(null); // { sessionId, methods, emailMasked }
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const applySession = (accessToken, userData) => {
    localStorage.setItem("token", accessToken);
    localStorage.removeItem("setup_token");
    axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
    setSetupToken(null);
    setMfaState(null);
    setToken(accessToken);
    setUser(userData);
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    if (data.mfa_required) {
      setMfaState({ sessionId: data.mfa_session_id, methods: data.methods, emailMasked: data.email_masked });
      return { kind: "mfa" };
    }
    if (data.setup_required) {
      localStorage.setItem("setup_token", data.setup_token);
      setSetupToken(data.setup_token);
      return { kind: "setup" };
    }
    return { kind: "unknown" };
  };

  const register = async (userData) => {
    const { data } = await axios.post(`${API_URL}/api/auth/register`, userData);
    if (data.setup_required) {
      localStorage.setItem("setup_token", data.setup_token);
      setSetupToken(data.setup_token);
      return { kind: "setup", email: data.email };
    }
    return { kind: "unknown" };
  };

  const verifyMfa = async (code, method) => {
    if (!mfaState) throw new Error("No MFA session");
    const { data } = await axios.post(`${API_URL}/api/auth/mfa/verify`, {
      mfa_session_id: mfaState.sessionId, code, method,
    });
    applySession(data.access_token, data.user);
    return data.user;
  };

  const sendEmailOtp = async () => {
    if (!mfaState) throw new Error("No MFA session");
    await axios.post(`${API_URL}/api/auth/mfa/email/send`, { mfa_session_id: mfaState.sessionId });
  };

  const enrolStart = async () => {
    const t = setupToken || token;
    const { data } = await axios.post(`${API_URL}/api/auth/mfa/enrol/start`, {}, {
      headers: { Authorization: `Bearer ${t}` },
    });
    return data; // { secret, otpauth_uri }
  };

  const enrolVerify = async (code) => {
    const t = setupToken || token;
    const { data } = await axios.post(`${API_URL}/api/auth/mfa/enrol/verify`, { code }, {
      headers: { Authorization: `Bearer ${t}` },
    });
    applySession(data.access_token, data.user);
    return data; // { backup_codes, access_token, user }
  };

  const stepUp = async (code, method = "totp") => {
    const { data } = await axios.post(`${API_URL}/api/auth/mfa/step-up`, { code, method });
    applySession(data.access_token, data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("setup_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setSetupToken(null);
    setMfaState(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser((prev) => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        setupToken,
        mfaState,
        isLoading,
        login,
        register,
        verifyMfa,
        stepUp,
        sendEmailOtp,
        enrolStart,
        enrolVerify,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
